import type { ComponentType } from 'react'

/**
 * Admin menu item for config-driven admin (primary items or sidebar).
 */
export type AdminMenuItem = {
  id: string
  label: string
  href: string
  description?: string
  icon?: string
  iconComponent?: ComponentType<{ className?: string }>
}

/**
 * Shell configuration for reusable app shell
 * Allows derivative apps to customize branding, metadata, and routes
 */
export type ShellConfig = {
  /** App metadata for Next.js */
  metadata: {
    title: string
    description: string
    favicon?: string
  }

  /** Branding configuration */
  branding: {
    /** App name shown in UI */
    appName: string
    /** Branding text shown in auth pages */
    brandingText?: string
    /** Logo URL or path */
    logoUrl?: string
    /** Footer copyright text */
    copyrightText?: string
  }

  /** Route configuration */
  routes: {
    /** Homepage route (default redirect after login) */
    homepage: string
    /** Sign in page route */
    signIn: string
    /** Register page route */
    register: string
    /** Password reset request route */
    forgotPassword: string
    /** Terms of service route */
    terms: string
    /** Privacy policy route */
    privacy: string
  }

  /** Theme configuration */
  theme: {
    /** RemixIcon CDN URL */
    remixIconCdn?: string
    /** Custom theme script (for dark mode initialization) */
    themeScript?: string
  }

  /** Session configuration */
  session: {
    /** Session timeout in minutes */
    timeoutMinutes?: number
    /** Warning before timeout in minutes */
    warningMinutes?: number
  }

  /** Route management configuration */
  routeManagement: {
    /** Routes managed by shell (auto-loaded from @korvo/shell) */
    shellManaged: {
      /** Admin routes managed by shell */
      admin?: boolean | string[]
      /** App home route */
      appHome?: boolean
      /** Static pages (terms, privacy, etc.) */
      staticPages?: boolean | string[]
    }
    /** Custom routes (not managed by shell, must exist in src/app) */
    custom?: string[]
  }

  /** TopBar configuration */
  topBar?: {
    /** Whether to show the TopBar (default: true) */
    enabled?: boolean
    /** When true, show topbar on /editor/ (e.g. when editor package has no internal Home). Default false. */
    showOnEditorPaths?: boolean
    /** Path prefixes where the topbar is hidden (app provides own home/nav). E.g. ['/chat/', '/admin/'] */
    hideOnPathPrefixes?: string[]
    /** Path prefixes where the topbar is hidden on desktop but shown on mobile (burger/nav). E.g. ['/chat/', '/editor/'] */
    showOnPathPrefixesOnlyWhenMobile?: string[]
    /** Path prefixes where the topbar is always shown on mobile (e.g. `/home` for profile/dark mode). Overrides hideOnPathPrefixes on mobile. */
    showOnMobileForPathPrefixes?: string[]
    /** Path prefixes where the topbar is shown but without side menu (no burger; home icon only). E.g. ['/home', '/auth', '/terms', '/help'] */
    pathPrefixesWithoutSideMenu?: string[]
    /** When set, side menu (hamburger) is shown only on these path prefixes (extra context). E.g. ['/chat', '/editor']. Omit = use pathPrefixesWithoutSideMenu to hide on specific paths. */
    pathPrefixesWithSideMenu?: string[]
    /** Path prefixes where the side menu (hamburger) is shown only on mobile; hidden on desktop. E.g. ['/admin']. */
    pathPrefixesWithSideMenuOnlyWhenMobile?: string[]
    /** Minimal topbar: only menu toggle (hamburger). Home, profile, dark mode live in the drawer. */
    minimal?: boolean
    /** Items on the left (before hamburger). Rendered in order. */
    leftItems?: TopBarItem[]
    /** Custom component for center (e.g. branding). */
    centerContent?: ComponentType<{ className?: string }>
    /** Items on the right (before dark mode / avatar). Rendered in order. */
    rightItems?: TopBarItem[]
    /** Show side menu hamburger (default: true) */
    showSideMenu?: boolean
    /** Show dark mode toggle (default: true; ignored when minimal) */
    showDarkModeToggle?: boolean
    /** Show user avatar (default: true; ignored when minimal) */
    showUserAvatar?: boolean
  }

  /** Footer configuration */
  footer?: {
    /** Whether to show the Footer (default: true) */
    enabled?: boolean
    /** Custom footer component. When set, used instead of the default footer (e.g. derivative app branding/links). */
    footerComponent?: ComponentType
  }

  /** Config-driven admin: primary items and advanced link */
  admin?: {
    /** Items in the simplified admin sidebar. If omitted, show full admin. */
    primaryItems?: AdminMenuItem[]
    /** Label for advanced/full admin link (default: "Advanced Settings"). False = hide. */
    advancedLabel?: string | false
    /** Sidebar subtitle (e.g. "Therapist controls") */
    subtitle?: string
    /** Custom dashboard component (replaces default AdminDashboardPage when set) */
    dashboard?: ComponentType
  }
}

/** Single item in TopBar left/right areas */
export type TopBarItem = {
  id: string
  label: string
  href?: string
  icon?: string
  /** Show only for these roles (e.g. ['admin']). Omit = show for all. */
  roles?: string[]
  /** Custom render. When set, href/label/icon are ignored. */
  render?: ComponentType
  /** Sort order (lower = earlier). Default 0. */
  order?: number
}

/**
 * Default shell configuration
 * Derivative apps should override this with their own config
 */
export const defaultShellConfig: ShellConfig = {
  metadata: {
    title: 'Korvo',
    description: 'Unified Workspace',
    favicon: '/favicon.ico',
  },
  branding: {
    appName: 'Korvo',
    brandingText: 'Access your workspace',
    copyrightText: '© 2025 Korvo. All rights reserved.',
  },
  routes: {
    homepage: '/home',
    signIn: '/auth/signin',
    register: '/auth/register',
    forgotPassword: '/auth/password/request',
    terms: '/terms',
    privacy: '/privacy',
  },
  theme: {
    remixIconCdn: 'https://cdn.jsdelivr.net/npm/remixicon@3.5.0/fonts/remixicon.css',
    themeScript: `
      try {
        if (localStorage.theme === 'dark' || (!('theme' in localStorage) && window.matchMedia('(prefers-color-scheme: dark)').matches)) {
          document.documentElement.classList.add('dark')
        } else {
          document.documentElement.classList.remove('dark')
        }
      } catch (_) {}
    `,
  },
  session: {
    timeoutMinutes: 30,
    warningMinutes: 5,
  },
  routeManagement: {
    shellManaged: {
      admin: true,
      appHome: true,
      staticPages: true,
    },
    custom: [],
  },
  topBar: {
    enabled: true,
    /** Hide topbar when path has own nav: /chat, /editor/:id (not /editor project selection). */
    hideOnPathPrefixes: ['/chat', '/editor/'],
    /** On these paths show topbar only on mobile (desktop gets full app nav). */
    showOnPathPrefixesOnlyWhenMobile: ['/chat'],
    /** Always show topbar on mobile for these paths (e.g. `/home` for profile/dark mode). */
    showOnMobileForPathPrefixes: ['/home'],
    /** Topbar with home icon only, no sidebar: home dashboard, auth, static pages, editor project selection. */
    pathPrefixesWithoutSideMenu: ['/home', '/auth', '/privacy', '/impressum', '/terms', '/help', '/editor'],
    /** Sidebar (hamburger) only where there is extra context (e.g. chat, settings, admin). */
    pathPrefixesWithSideMenu: ['/chat', '/settings', '/admin'],
    /** On admin, show side menu button only on mobile; desktop has its own sidebar. */
    pathPrefixesWithSideMenuOnlyWhenMobile: ['/admin'],
  },
  footer: {
    enabled: true,
  },
  // admin omitted = full admin
}

